import React, { useState, useEffect, memo } from 'react';
import { useTranslation } from 'next-i18next';

interface TypewriterPlaceholderProps {
  texts: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseTime?: number;
}

// 循环打字机效果 Hook
function useTypewriterLoop(
  texts: string[],
  typingSpeed: number = 100,
  deletingSpeed: number = 50,
  pauseTime: number = 2000
) {
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (!texts || texts.length === 0) return;

    let currentTextIndex = 0;
    let currentCharIndex = 0;
    let isDeleting = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const type = () => {
      const fullText = texts[currentTextIndex];

      if (isDeleting) {
        // 删除模式
        if (currentCharIndex > 0) {
          currentCharIndex--;
          setDisplayedText(fullText.substring(0, currentCharIndex));
          timeoutId = setTimeout(type, deletingSpeed);
        } else {
          // 删除完毕,切换到下一个文本
          isDeleting = false;
          currentTextIndex = (currentTextIndex + 1) % texts.length;
          timeoutId = setTimeout(type, 500); // 短暂停顿后开始下一句
        }
      } else {
        // 打字模式
        if (currentCharIndex < fullText.length) {
          currentCharIndex++;
          setDisplayedText(fullText.substring(0, currentCharIndex));
          timeoutId = setTimeout(type, typingSpeed);
        } else {
          // 打字完毕,准备删除
          isDeleting = true;
          timeoutId = setTimeout(type, pauseTime); // 停顿后开始删除
        }
      }
    };

    // 开始打字动画
    timeoutId = setTimeout(type, 500);

    // 清理函数
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [texts, typingSpeed, deletingSpeed, pauseTime]);

  // 光标闪烁效果
  useEffect(() => {
    const cursorInterval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, []);

  return { displayedText, showCursor };
}

const TypewriterPlaceholder: React.FC<TypewriterPlaceholderProps> = memo(
  ({ texts, typingSpeed = 100, deletingSpeed = 50, pauseTime = 2000 }) => {
    const { displayedText, showCursor } = useTypewriterLoop(texts, typingSpeed, deletingSpeed, pauseTime);
    const { t } = useTranslation();

    return (
      <div className="pointer-events-none text-[#717182]">
        <span className="text-base leading-6 tracking-[-0.3125px] mr-2">{t('e.g.')}</span>
        <span className="text-base leading-6 tracking-[-0.3125px]">{displayedText}</span>
        <span
          className={`inline-block w-0.5 h-[1em] bg-[#030213]/70 ml-1 transition-opacity duration-100 ${
            showCursor ? 'opacity-75' : 'opacity-0'
          }`}
        />
      </div>
    );
  }
);

TypewriterPlaceholder.displayName = 'TypewriterPlaceholder';

export default TypewriterPlaceholder;
